import { useState, useEffect } from 'react';
import { PhoneBookRequest } from '../../types';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/StatusBadge';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { displayPhone } from '../../utils/phone';
import { AdminRemovalRequests } from './RemovalRequests';

export const AdminRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<PhoneBookRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PhoneBookRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('phone_book_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setRequests((data ?? []) as PhoneBookRequest[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleApprove = async (request: PhoneBookRequest) => {
    setProcessing(true);
    try {
      const { error: insertError } = await supabase.from('phone_book').insert({
        marathi_name: request.marathi_name,
        name: request.name,
        phone_number: request.phone_number,
        alternate_phone_number: request.alternate_phone_number,
        shivar: request.shivar,
        village: request.village,
        taluka: request.taluka,
        district: request.district,
        address: request.address,
        category: request.category,
        status: 'APPROVED',
        created_by: user?.id ?? null,
        approved_by: user?.id ?? null,
        approved_at: new Date().toISOString(),
      });
      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from('phone_book_requests')
        .update({
          status: 'APPROVED',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', request.id);
      if (updateError) throw updateError;

      setSelected(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (request: PhoneBookRequest) => {
    setProcessing(true);
    try {
      const { error: updateError } = await supabase
        .from('phone_book_requests')
        .update({
          status: 'BLOCKED',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: rejectionReason,
        })
        .eq('id', request.id);
      if (updateError) throw updateError;

      setSelected(null);
      setRejectionReason('');
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setProcessing(false);
    }
  };

  const [tab, setTab] = useState<'contact-requests' | 'removal-requests'>('contact-requests');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Requests</h1>
        <p className="text-sm text-gray-600">Review and approve employee submissions and number removal requests.</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab('contact-requests')}
          className={`rounded px-4 py-2 text-sm font-medium ${tab === 'contact-requests' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Contact Requests
        </button>
        <button
          type="button"
          onClick={() => setTab('removal-requests')}
          className={`rounded px-4 py-2 text-sm font-medium ${tab === 'removal-requests' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Removal Requests
        </button>
      </div>
      {tab === 'contact-requests' ? (
        loading ? (
          <Loading full />
        ) : error ? (
          <ErrorAlert message={error} onRetry={loadRequests} />
        ) : (
          <div className="space-y-6">
            {requests.length === 0 ? (
              <Empty title="No requests" message="Employees have not submitted any contacts yet." />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Marathi Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Village</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Notes</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {requests.map((request: PhoneBookRequest) => (
                      <tr key={request.id}>
                        <td className="px-4 py-3 text-sm font-medium text-blue-800" lang="mr">{request.marathi_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{request.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{displayPhone(request.phone_number)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{request.village ?? '-'}</td>
                        <td className="px-4 py-3"><StatusBadge status={request.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{request.notes ?? '-'}</td>
                        <td className="px-4 py-3 text-right">
                          {request.status === 'UNDER_REVIEW' && (
                            <button
                              onClick={() => setSelected(request)}
                              className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                            >
                              Review
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selected && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-bold text-gray-900">Review Request</h2>
                <div className="mt-2 space-y-1 text-sm text-gray-700">
                  <p><span className="font-medium">Marathi:</span> {selected.marathi_name}</p>
                  <p><span className="font-medium">Name:</span> {selected.name}</p>
                  <p><span className="font-medium">Phone:</span> {displayPhone(selected.phone_number)}</p>
                </div>
                <div className="mt-4 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Rejection reason (required for reject)</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    rows={2}
                  />
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => setSelected(null)}
                    className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleReject(selected)}
                    disabled={processing || !rejectionReason.trim()}
                    className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selected)}
                    disabled={processing}
                    className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      ) : (
        <AdminRemovalRequests />
      )}
    </div>
  );
};
